import React, { useEffect, useRef } from 'react';
import { X, CreditCard } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Classes pour la zone de contenu (ex. scroll, hauteur max) */
  bodyClassName?: string;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = 'md',
  bodyClassName = '',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Fermer la modal avec la touche Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    // Désactiver le scroll du body quand la modal est ouverte
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  // Fermer la modal quand on clique à l'extérieur
  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const maxWidthClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-xl',
    xl: 'max-w-2xl',
    '2xl': 'max-w-4xl',
  };

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-canvas/70 backdrop-blur-sm"
      onClick={handleOutsideClick}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div
        ref={modalRef}
        className={`bg-surface border border-line rounded-card shadow-[0_24px_64px_-24px_rgb(0_0_0/0.8)] overflow-hidden w-full ${maxWidthClasses[maxWidth]} mx-4 transform transition-all duration-300 ease-in-out`}
        style={{ position: 'relative', zIndex: 999999 }}
      >
        <div className="border-b border-line px-4 py-3 sm:p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {typeof title === 'string' ? (
                <>
                  <CreditCard className="w-5 h-5 text-accent-soft mr-2" />
                  <h3 className="text-xl font-extrabold tracking-[-0.02em] text-fg">{title}</h3>
                </>
              ) : (
                <h3 className="text-xl font-extrabold tracking-[-0.02em] text-fg">{title}</h3>
              )}
            </div>
            <button
              onClick={onClose}
              aria-label="Fermer"
              className="-mr-2 flex h-11 w-11 shrink-0 items-center justify-center text-fg-muted hover:text-fg transition-colors md:mr-0 md:h-auto md:w-auto"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className={`p-4 sm:p-5 ${bodyClassName}`.trim()}>
          {children}
        </div>
      </div>
    </div>
  );
}; 